import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground active:bg-primary/90 md:hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground active:bg-destructive/90 md:hover:bg-destructive/90",
        outline:
          "border border-input bg-background active:bg-accent md:hover:bg-accent active:text-accent-foreground md:hover:text-accent-foreground",
        outlineRound:
          "border border-input bg-background active:bg-accent md:hover:bg-accent active:text-accent-foreground md:hover:text-accent-foreground rounded-full shadow-sm",
        outlineNoBorder:
          "bg-background active:bg-accent md:hover:bg-accent active:text-accent-foreground md:hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground active:bg-secondary/80 md:hover:bg-secondary/50",
        ghost: "active:bg-accent active:text-accent-foreground",
        link: "text-primary underline-offset-4 active:underline md:hover:underline",
        // ai: "bg-purple-100 text-purple-600 active:bg-purple-200 md:hover:bg-purple-200 rounded-full text-xs",
        // ai: "bg-gray-800 text-purple-200 active:bg-purple-200 md:hover:bg-purple-200 rounded-full text-sm font-semibold",
        ai: "bg-purple-500 text-white active:bg-purple-400 md:hover:bg-purple-400 rounded-full text-sm font-semibold",
        // ai: "bg-gray-700 ring-inset text-white border-purple-400 active:bg-purple-200 md:hover:bg-purple-200 rounded-full text-sm font-semibold",
        dark: "bg-white text-black active:bg-gray-100 md:hover:bg-gray-100 shadow-sm border",
        custom: "",
      },
      size: {
        default: "px-4 py-2",
        sm: "h-8 rounded-md px-3 text-sm",
        lg: "h-10 rounded-md px-8",
        // icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
